import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFileExcel } from "react-icons/ai";
import { FiBookOpen, FiRefreshCw } from "react-icons/fi";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { Month } from "../../OtherData/Month";
import { errorAlert, infoAlert } from "../../Components/Alerts";
import { TaxSalesExcelForm } from "../../Print/TaxSalesExcelForm";
import { TaxSalesExcelFormNoVAT } from "../../Print/TaxSalesExcelFormNoVAT";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { AddActionsButton } from "../../Components/AddElementsButton";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./TaxSalesBookPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const toNumber = (value) => Number.parseFloat(value) || 0;

const emptyVatTotals = {
  Shitjet_Pa_Kreditim: 0,
  Shitjet_Sherbimeve: 0,
  Shitjet_Me_Ngarkese_Te_Kundert: 0,
  Shitjet_Liruara_Me_Kreditim: 0,
  Total_Shitjet_Liruara_Me_Kreditim: 0,
  Eksportet: 0,
  Shitjet18: 0,
  NotaDebitore18: 0,
  BorgjiKeq18: 0,
  Rregullimet18: 0,
  NgarkesaKundert18: 0,
  TotalVAT18: 0,
  Shitjet8: 0,
  NotaDebitore8: 0,
  BorgjiKeq8: 0,
  Rregullimet8: 0,
  TotalVAT8: 0,
  Total_TVSH_8_dhe_18: 0,
};

const emptyNoVatTotals = {
  ShitjeVendore: 0,
  EksporteSherbimeve: 0,
  EksporteProdukteve: 0,
  Total: 0,
};

const buildVatTotals = (items) => items.reduce((acc, item) => ({
  Shitjet_Pa_Kreditim: acc.Shitjet_Pa_Kreditim + toNumber(item.Shitjet_Pa_Kreditim),
  Shitjet_Sherbimeve: acc.Shitjet_Sherbimeve + toNumber(item.Shitjet_Sherbimeve),
  Shitjet_Me_Ngarkese_Te_Kundert: acc.Shitjet_Me_Ngarkese_Te_Kundert + toNumber(item.Shitjet_Me_Ngarkese_Te_Kundert),
  Shitjet_Liruara_Me_Kreditim: acc.Shitjet_Liruara_Me_Kreditim + toNumber(item.Shitjet_Liruara_Me_Kreditim),
  Total_Shitjet_Liruara_Me_Kreditim: acc.Total_Shitjet_Liruara_Me_Kreditim + toNumber(item.Total_Shitjet_Liruara_Me_Kreditim),
  Eksportet: acc.Eksportet + toNumber(item.Eksportet),
  Shitjet18: acc.Shitjet18 + toNumber(item.Shitjet18),
  NotaDebitore18: acc.NotaDebitore18 + toNumber(item.NotaDebitore18),
  BorgjiKeq18: acc.BorgjiKeq18 + toNumber(item.BorgjiKeq18),
  Rregullimet18: acc.Rregullimet18 + toNumber(item.Rregullimet18),
  NgarkesaKundert18: acc.NgarkesaKundert18 + toNumber(item.NgarkesaKundert18),
  TotalVAT18: acc.TotalVAT18 + toNumber(item.TotalVAT18),
  Shitjet8: acc.Shitjet8 + toNumber(item.Shitjet8),
  NotaDebitore8: acc.NotaDebitore8 + toNumber(item.NotaDebitore8),
  BorgjiKeq8: acc.BorgjiKeq8 + toNumber(item.BorgjiKeq8),
  Rregullimet8: acc.Rregullimet8 + toNumber(item.Rregullimet8),
  TotalVAT8: acc.TotalVAT8 + toNumber(item.TotalVAT8),
  Total_TVSH_8_dhe_18: acc.Total_TVSH_8_dhe_18 + toNumber(item.Total_TVSH_8_dhe_18),
}), { ...emptyVatTotals });

const buildNoVatTotals = (items) => items.reduce((acc, item) => ({
  ShitjeVendore: acc.ShitjeVendore + toNumber(item.ShitjeVendore),
  EksporteSherbimeve: acc.EksporteSherbimeve + toNumber(item.EksporteSherbimeve),
  EksporteProdukteve: acc.EksporteProdukteve + toNumber(item.EksporteProdukteve),
  Total: acc.Total + toNumber(item.ShitjeVendore) + toNumber(item.EksporteSherbimeve) + toNumber(item.EksporteProdukteve),
}), { ...emptyNoVatTotals });

function formatDateForDisplay(date) {
  if (!date) {
    return "";
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const raw = String(date).trim();
  if (!raw) {
    return "";
  }

  const dottedMatch = raw.match(/^(\d{2})[.](\d{2})[.](\d{4})$/);
  if (dottedMatch) {
    return raw;
  }

  const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${day}.${month}.${year}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  const parsedDate = new Date(raw);
  if (Number.isNaN(parsedDate.getTime())) {
    return raw;
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function TaxSalesBookPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [data, setData] = useState([]);
  const [dataNoVAT, setDataNoVAT] = useState([]);

  const [month, setMonth] = useState(0);
  const [ngaData, setNgaData] = useState("");
  const [deriData, setDeriData] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [showMonthFilter, setShowMonthFilter] = useState(true);
  const [vatDeclarant, setVatDeclarant] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingNoVat, setLoadingNoVat] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({ ...emptyVatTotals, ...emptyNoVatTotals });

  const [tm, setTm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const allTM = useMemo(() => ([
    {
      id: 1,
      title: "TM1",
      firstDate: `${fiscalYear}-01-01`,
      lastDate: `${fiscalYear}-03-31`,
    },
    {
      id: 2,
      title: "TM2",
      firstDate: `${fiscalYear}-04-01`,
      lastDate: `${fiscalYear}-06-30`,
    },
    {
      id: 3,
      title: "TM3",
      firstDate: `${fiscalYear}-07-01`,
      lastDate: `${fiscalYear}-09-30`,
    },
    {
      id: 4,
      title: "TM4",
      firstDate: `${fiscalYear}-10-01`,
      lastDate: `${fiscalYear}-12-31`,
    },
  ]), [fiscalYear]);

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);
  }, [fiscalYear]);

  const openDateFilter = () => {
    setShowMonthFilter(false);
    setMonth(0);
    setData([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const openMonthFilter = () => {
    setShowMonthFilter(true);
    setNgaData("");
    setDeriData("");
    setData([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const buildPeriodParams = () => {
    const params = {};

    if (month && month >= 1 && month <= 12) {
      params.month = month;
      params.fiscalYear = fiscalYear;
    } else if (ngaData && deriData) {
      params.firstDate = ngaData;
      params.secondDate = deriData;
    } else {
      return null;
    }

    return params;
  };

  const fetchCompaniesData = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
      setVatDeclarant(Boolean(response?.data?.[0]?.VATDeclarant));
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const fetchData = async () => {
    const periodParams = buildPeriodParams();
    if (!periodParams) {
      setData([]);
      setRowCount(0);
      setTotals({ ...emptyVatTotals, ...emptyNoVatTotals });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "TaxReport/salesbook-grid", {
        headers,
        params: {
          ...periodParams,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items || []),
          quickFilter: quickFilter.join(" "),
        },
      });

      const payload = response?.data;
      const items = Array.isArray(payload) ? payload : (payload?.items || payload?.data || []);
      const total = Array.isArray(payload)
        ? items.length
        : Number(payload?.total ?? payload?.totalRows ?? items.length);

      setData(items);
      setRowCount(total);

      try {
        const totalsResponse = await axios.get(ApiLink.API_URL + "TaxReport/salesbook-grid", {
          headers,
          params: {
            ...periodParams,
            page: 0,
            pageSize: Math.max(total, items.length, 1),
            sort: JSON.stringify(sortModel),
            filter: JSON.stringify(filterModel.items || []),
            quickFilter: quickFilter.join(" "),
          },
        });
        const totalsPayload = totalsResponse?.data;
        const totalsItems = Array.isArray(totalsPayload)
          ? totalsPayload
          : (totalsPayload?.items || totalsPayload?.data || []);
        setTotals(buildVatTotals(totalsItems));
      } catch (totalsError) {
        setTotals({ ...emptyVatTotals, ...emptyNoVatTotals });
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        infoAlert("Ju nuk keni qasje në këto të dhëna.");
      } else if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDataNoVAT = async (first, second) => {
    if (!first || !second) {
      setDataNoVAT([]);
      setTotals((prev) => ({ ...prev, ...emptyNoVatTotals }));
      return;
    }

    setLoadingNoVat(true);
    try {
      const response = await axios.get(ApiLink.API_URL + "TaxReport/salesbooknovat", {
        headers,
        params: {
          firstDate: first,
          secondDate: second,
        },
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      setDataNoVAT(items);
      setTotals((prev) => ({ ...prev, ...buildNoVatTotals(items) }));
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    } finally {
      setLoadingNoVat(false);
    }
  };

  useEffect(() => {
    fetchCompaniesData();
  }, []);

  useEffect(() => {
    if (!vatDeclarant) {
      return;
    }

    fetchData();
  }, [month, fiscalYear, ngaData, deriData, paginationModel, sortModel, filterModel, quickFilter, vatDeclarant]);

  useEffect(() => {
    if (vatDeclarant || !startDate || !endDate) {
      return;
    }

    fetchDataNoVAT(startDate, endDate);
  }, [vatDeclarant, startDate, endDate]);

  const handleTmChange = (event) => {
    const selectedId = Number(event.target.value);
    setTm(selectedId);
    const selectedTM = allTM.find((item) => item.id === selectedId);
    if (!selectedTM) {
      setStartDate("");
      setEndDate("");
      setDataNoVAT([]);
      return;
    }

    setStartDate(selectedTM.firstDate);
    setEndDate(selectedTM.lastDate);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "DataFatures", headerName: "Data Fatures", minWidth: 130, width: 140 },
    { field: "NumriFatures", headerName: "Numri Fatures", minWidth: 180, width: 220 },
    { field: "EmriBleresit", headerName: "Emri Bleresit", minWidth: 220, width: 250 },
    { field: "IdentificationNumber", headerName: "Numri Identifikues", minWidth: 180, width: 210 },
    { field: "VATNumber", headerName: "Numri i TVSH-se", minWidth: 170, width: 190 },
    {
      field: "Shitjet_Pa_Kreditim",
      headerName: "Shitjet Pa Kreditim",
      minWidth: 150,
      width: 170,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Shitjet_Sherbimeve",
      headerName: "Shitjet Sherbimeve",
      minWidth: 150,
      width: 170,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Shitjet_Me_Ngarkese_Te_Kundert",
      headerName: "Shitjet Me Ngarkese Te Kundert",
      minWidth: 180,
      width: 220,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Shitjet_Liruara_Me_Kreditim",
      headerName: "Shitjet Liruara Me Kreditim",
      minWidth: 180,
      width: 220,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Total_Shitjet_Liruara_Me_Kreditim",
      headerName: "Total Shitjet Liruara Me Kreditim",
      minWidth: 200,
      width: 250,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Eksportet",
      headerName: "Eksportet",
      minWidth: 130,
      width: 140,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Shitjet18",
      headerName: "Shitjet 18",
      minWidth: 120,
      width: 130,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "NotaDebitore18",
      headerName: "Nota Debitore 18",
      minWidth: 140,
      width: 160,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "BorgjiKeq18",
      headerName: "Borgji Keq 18",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Rregullimet18",
      headerName: "Rregullimet 18",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "NgarkesaKundert18",
      headerName: "Ngarkesa Kundert 18",
      minWidth: 160,
      width: 185,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "TotalVAT18",
      headerName: "Total VAT 18",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Shitjet8",
      headerName: "Shitjet 8",
      minWidth: 120,
      width: 130,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "NotaDebitore8",
      headerName: "Nota Debitore 8",
      minWidth: 140,
      width: 160,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "BorgjiKeq8",
      headerName: "Borgji Keq 8",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Rregullimet8",
      headerName: "Rregullimet 8",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "TotalVAT8",
      headerName: "Total VAT 8",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Total_TVSH_8_dhe_18",
      headerName: "Total TVSH 8 dhe 18",
      minWidth: 165,
      width: 190,
      valueFormatter: (value) => formatCurrency(value),
    },
  ]), []);

  const columnsNoVAT = useMemo(() => ([
    { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
    { field: "Date", headerName: "Data Fatures", minWidth: 120, width: 130 },
    { field: "InvoiceNr", headerName: "Numri Fatures", minWidth: 180, width: 220 },
    { field: "ActionType", headerName: "Lloji transaksionit", minWidth: 160, width: 190 },
    { field: "TransactionType", headerName: "Import/Export", minWidth: 140, width: 170 },
    { field: "AccountName", headerName: "Emri Bleresit", minWidth: 220, width: 250 },
    { field: "IdentificationNumber", headerName: "Numri Identifikues", minWidth: 180, width: 210 },
    {
      field: "ShitjeVendore",
      headerName: "Shitje vendore",
      minWidth: 140,
      width: 170,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "EksporteSherbimeve",
      headerName: "Eksporte sherbimeve",
      minWidth: 160,
      width: 190,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "EksporteProdukteve",
      headerName: "Eksporte",
      minWidth: 130,
      width: 150,
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: "Total",
      headerName: "Shitje Totale",
      minWidth: 140,
      width: 170,
      valueFormatter: (value) => formatCurrency(value),
    },
  ]), []);

  const rows = useMemo(() => (
    data.map((item, index) => ({
      id: `${item.NumriFatures || "tax-sales"}-${index}`,
      rowNumber: item.Nr || (paginationModel.page * paginationModel.pageSize + index + 1),
      DataFatures: formatDateForDisplay(item.DataFatures),
      NumriFatures: item.NumriFatures || "",
      EmriBleresit: item.EmriBleresit || "",
      IdentificationNumber: item.IdentificationNumber || "",
      VATNumber: item.VATNumber || "",
      Shitjet_Pa_Kreditim: item.Shitjet_Pa_Kreditim || 0,
      Shitjet_Sherbimeve: item.Shitjet_Sherbimeve || 0,
      Shitjet_Me_Ngarkese_Te_Kundert: item.Shitjet_Me_Ngarkese_Te_Kundert || 0,
      Shitjet_Liruara_Me_Kreditim: item.Shitjet_Liruara_Me_Kreditim || 0,
      Total_Shitjet_Liruara_Me_Kreditim: item.Total_Shitjet_Liruara_Me_Kreditim || 0,
      Eksportet: item.Eksportet || 0,
      Shitjet18: item.Shitjet18 || 0,
      NotaDebitore18: item.NotaDebitore18 || 0,
      BorgjiKeq18: item.BorgjiKeq18 || 0,
      Rregullimet18: item.Rregullimet18 || 0,
      NgarkesaKundert18: item.NgarkesaKundert18 || 0,
      TotalVAT18: item.TotalVAT18 || 0,
      Shitjet8: item.Shitjet8 || 0,
      NotaDebitore8: item.NotaDebitore8 || 0,
      BorgjiKeq8: item.BorgjiKeq8 || 0,
      Rregullimet8: item.Rregullimet8 || 0,
      TotalVAT8: item.TotalVAT8 || 0,
      Total_TVSH_8_dhe_18: item.Total_TVSH_8_dhe_18 || 0,
      rowInfo: item,
    }))
  ), [data, paginationModel.page, paginationModel.pageSize]);

  const rowsNoVATAll = useMemo(() => (
    dataNoVAT.map((item, index) => {
      const total = (parseFloat(item.ShitjeVendore) || 0)
        + (parseFloat(item.EksporteSherbimeve) || 0)
        + (parseFloat(item.EksporteProdukteve) || 0);

      return {
        id: `${item.InvoiceNr || "tax-sales-novat"}-${index}`,
        rowNumber: index + 1,
        Date: formatDateForDisplay(item.Date),
        InvoiceNr: item.ActionType === "IND"
          ? `IND-${fiscalYear}-${item.InvoiceNr}`
          : (item.InvoiceNr || ""),
        ActionType: item.ActionType || "",
        TransactionType: item.TransactionType || "",
        AccountName: item.AccountName || "",
        IdentificationNumber: item.IdentificationNumber || "",
        ShitjeVendore: item.ShitjeVendore || 0,
        EksporteSherbimeve: item.EksporteSherbimeve || 0,
        EksporteProdukteve: item.EksporteProdukteve || 0,
        Total: total,
        rowInfo: item,
      };
    })
  ), [dataNoVAT, fiscalYear]);

  const rowsNoVAT = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return rowsNoVATAll.slice(start, end);
  }, [rowsNoVATAll, paginationModel]);

  const fetchAllRowsForExport = async () => {
    if (!vatDeclarant) {
      return dataNoVAT;
    }

    const periodParams = buildPeriodParams();
    if (!periodParams) {
      return [];
    }

    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
    const response = await axios.get(ApiLink.API_URL + "TaxReport/salesbook-grid", {
      headers,
      params: {
        ...periodParams,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items || []),
        quickFilter: quickFilter.join(" "),
      },
    });

    const payload = response?.data;
    if (Array.isArray(payload)) {
      return payload;
    }

    return payload?.items || payload?.data || [];
  };

  const downloadWorkbook = (workbook, fileName) => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleExportExcel = async () => {
    try {
      if (vatDeclarant) {
        const exportRows = await fetchAllRowsForExport();
        if (!exportRows.length) {
          infoAlert("Nuk ka të dhëna për eksport.");
          return;
        }

        const workbook = TaxSalesExcelForm(exportRows);
        const fileName = month === 0
          ? `LSH_Periudha_nga_${formatDateForDisplay(ngaData)}_deri_${formatDateForDisplay(deriData)}.xlsx`
          : `LSH_${month}_${fiscalYear}.xlsx`;
        downloadWorkbook(workbook, fileName);
        return;
      }

      if (!dataNoVAT.length) {
        infoAlert("Nuk ka të dhëna për eksport.");
        return;
      }

      const workbook = TaxSalesExcelFormNoVAT(dataNoVAT);
      const fileName = `LSH_TM${tm || ""}_${fiscalYear}.xlsx`;
      downloadWorkbook(workbook, fileName);
    } catch (error) {
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në eksport: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur gjatë eksportit.");
      }
    }
  };

  const filterToolbar = vatDeclarant ? (
    <>
      <button
        type="button"
        className="RSDG__toolButton TSBP__toolbarSwitch"
        onClick={() => {
          if (showMonthFilter) {
            openDateFilter();
          } else {
            openMonthFilter();
          }
        }}
        aria-label="Ndrysho menyren e filtrimit"
        title={`Ndrysho filtrin: ${showMonthFilter ? "Muaji" : "Datat"}`}
      >
        <span className="RSDG__toolButtonContent">
          <FiRefreshCw size={14} className={`TSBP__toolbarSwitchIcon${showMonthFilter ? "" : " TSBP__toolbarSwitchIcon--rotated"}`} />
          <span>{showMonthFilter ? "Muaji" : "Datat"}</span>
        </span>
      </button>

      {showMonthFilter ? (
        <TextField
          className="TSBP__toolbarField TSBP__toolbarField--month"
          select
          size="small"
          label="Muaji"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          required
        >
          {Month.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {fiscalYear} {item.month}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <div className="TSBP__toolbarDateWrap TSBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy
              label="Nga"
              value={ngaData}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(value) => {
                setNgaData(value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              fullWidth={false}
            />
          </div>
          <div className="TSBP__toolbarDateWrap TSBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy
              label="Deri"
              value={deriData}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(value) => {
                setDeriData(value);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              fullWidth={false}
            />
          </div>
        </>
      )}
    </>
  ) : (
    <TextField
      className="TSBP__toolbarField TSBP__toolbarField--month"
      select
      size="small"
      label="Perzgjedh 3-Mujorin"
      value={tm}
      onChange={handleTmChange}
      required
    >
      {allTM.map((item) => (
        <MenuItem key={item.id} value={item.id}>
          {item.title}
        </MenuItem>
      ))}
    </TextField>
  );

  const hasSelectedPeriod = vatDeclarant
    ? ((month && month >= 1 && month <= 12) || (ngaData && deriData))
    : Boolean(tm);

  const emptyGridMessage = hasSelectedPeriod
    ? "Nuk ka te dhena."
    : (vatDeclarant
      ? "Ju lutem zgjidhni periudhen (muajin ose datat)."
      : "Ju lutem zgjidhni 3-mujorin.");

  return (
    <div className="Crud__Container TSBP__page">
      <div className="TSBP__content">
        <CrudPageHeader
          icon={FiBookOpen}
          title="Libri i Shitjes per ATK"
          description="Ky format i librit te shitjeve eshte i pershtatur per ngarkim ne sistemin e ATK-se."
          className="TSBP__header"
        />

        <div className="TSBP__reportGrid">
          <ReusableAccountBooksGrid
            columns={vatDeclarant ? columns : columnsNoVAT}
            rows={vatDeclarant ? rows : rowsNoVAT}
            loading={vatDeclarant ? loading : loadingNoVat}
            rowCount={vatDeclarant ? rowCount : rowsNoVATAll.length}
            height={650}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            showQuickSearch={false}
            showExportButton={false}
            toolbarActions={(
              <AddActionsButton
                text="XLSX"
                onClick={handleExportExcel}
                icon={<AiOutlineFileExcel />}
                className="RSDG__toolButton TSBP__gridActionBtn--xlsx"
              />
            )}
            toolbarRightActions={filterToolbar}
            showViewAction={false}
            showEditAction={false}
            showDeleteAction={false}
            emptyMessage={emptyGridMessage}
            summaryFooter={vatDeclarant
              ? {
                labelField: "VATNumber",
                label: "Totali",
                values: {
                  Shitjet_Pa_Kreditim: formatCurrency(totals.Shitjet_Pa_Kreditim),
                  Shitjet_Sherbimeve: formatCurrency(totals.Shitjet_Sherbimeve),
                  Shitjet_Me_Ngarkese_Te_Kundert: formatCurrency(totals.Shitjet_Me_Ngarkese_Te_Kundert),
                  Shitjet_Liruara_Me_Kreditim: formatCurrency(totals.Shitjet_Liruara_Me_Kreditim),
                  Total_Shitjet_Liruara_Me_Kreditim: formatCurrency(totals.Total_Shitjet_Liruara_Me_Kreditim),
                  Eksportet: formatCurrency(totals.Eksportet),
                  Shitjet18: formatCurrency(totals.Shitjet18),
                  NotaDebitore18: formatCurrency(totals.NotaDebitore18),
                  BorgjiKeq18: formatCurrency(totals.BorgjiKeq18),
                  Rregullimet18: formatCurrency(totals.Rregullimet18),
                  NgarkesaKundert18: formatCurrency(totals.NgarkesaKundert18),
                  TotalVAT18: formatCurrency(totals.TotalVAT18),
                  Shitjet8: formatCurrency(totals.Shitjet8),
                  NotaDebitore8: formatCurrency(totals.NotaDebitore8),
                  BorgjiKeq8: formatCurrency(totals.BorgjiKeq8),
                  Rregullimet8: formatCurrency(totals.Rregullimet8),
                  TotalVAT8: formatCurrency(totals.TotalVAT8),
                  Total_TVSH_8_dhe_18: formatCurrency(totals.Total_TVSH_8_dhe_18),
                },
              }
              : {
                labelField: "AccountName",
                label: "Totali",
                values: {
                  ShitjeVendore: formatCurrency(totals.ShitjeVendore),
                  EksporteSherbimeve: formatCurrency(totals.EksporteSherbimeve),
                  EksporteProdukteve: formatCurrency(totals.EksporteProdukteve),
                  Total: formatCurrency(totals.Total),
                },
              }}
          />
        </div>
      </div>
    </div>
  );
}
